import { DexFormFieldView, DexEntityTypeEnum } from '@mark43/cad-api';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { Box } from '@cad-arc';
import { FormSelect } from '../../forms/integrated-form-controls/form-select';
import { FormCreatableSelect } from '../../forms/integrated-form-controls/form-creatable-select';
import { FormInput } from '../../forms/integrated-form-controls/form-input';
import { useIntegratedForm } from '../../forms/use-integrated-form';
import { useWatch } from 'react-hook-form';
import { useOriAliases } from '../../common/hooks/use-ori-aliases';
import { widthsToPercentageValues, widthsToMinimumValues } from './helpers';
import { useSelectedOriAlias } from './use-selected-ori-alias';
import { useBootstrapState } from '../../bootstrap/bootstrap-provider';
import { useShouldShowRequestorSelect } from './use-should-show-requestor-select';
import { useShouldShowAttentionField } from './use-should-show-attention-field';
import { shouldShowImageIndicator } from './should-show-image-indicator';

const requiredRule = { required: 'Required' };

export function DexField({
    dexFieldView,
    autoFocus,
}: {
    dexFieldView: DexFormFieldView;
    autoFocus?: boolean;
}): React.ReactElement {
    const { form } = useIntegratedForm();
    const oriAliases = useOriAliases();
    const { changeOriAlias } = useEnabledFeatures();
    const { setSelectedOriAliasId } = useSelectedOriAlias();
    const { dexRegion } = useBootstrapState();

    const inquiryTypeCode = useWatch({
        control: form.control,
        name: 'params.inquiryTypeCode'
    });

    const shouldShowRequestor = useShouldShowRequestorSelect(
        DexEntityTypeEnum.PERSON.name,
        dexRegion,
        inquiryTypeCode
    );
    const shouldShowAttention = useShouldShowAttentionField(
        DexEntityTypeEnum.PERSON.name,
        dexRegion,
        inquiryTypeCode
    );
    const shouldShowImageIndicatorField = shouldShowImageIndicator(
        DexEntityTypeEnum.PERSON.name,
        dexRegion,
        inquiryTypeCode
    );

    if (dexFieldView.name === 'attention' && !shouldShowAttention) {
        return <></>;
    }

    if (dexFieldView.name === 'requestor' && !shouldShowRequestor) {
        return <></>;
    }

    if (dexFieldView.name === 'imageIndicator' && !shouldShowImageIndicatorField) {
        return <></>;
    }

    if (oriAliases.length && dexFieldView.name === 'queryRegion') {
        // Note that oriAliasOptions do not come down with the dexFieldView. They are fetched separately on bootstrap.
        const options = oriAliases.map((o) => ({ ...o, value: o.value.toString(10) }));

        return (
            <Box
                minWidth={widthsToMinimumValues[dexFieldView.width]}
                width={widthsToPercentageValues[dexFieldView.width]}
            >
                <FormSelect
                    name="oriAliasId"
                    label={dexFieldView.label}
                    options={options}
                    control={form.control}
                    rules={dexFieldView.required ? requiredRule : undefined}
                    disableSetFullOptionAsValue
                    isDisabled={!changeOriAlias}
                    onChange={(e) => {
                        const id = (e.target as HTMLInputElement).value;
                        if (id) {
                            setSelectedOriAliasId(id);
                        }
                    }}
                />
            </Box>
        );
    }

    if (dexFieldView.options && dexFieldView.options.length > 0) {
        return (
            <Box
                minWidth={widthsToMinimumValues[dexFieldView.width]}
                width={widthsToPercentageValues[dexFieldView.width]}
            >
                <FormCreatableSelect
                    name={`params.${dexFieldView.name}`}
                    autoFocus={autoFocus}
                    label={dexFieldView.label}
                    options={dexFieldView.options}
                    control={form.control}
                    rules={dexFieldView.required ? requiredRule : undefined}
                    disableSetFullOptionAsValue
                />
            </Box>
        );
    } else {
        return (
            <Box
                minWidth={widthsToMinimumValues[dexFieldView.width]}
                width={widthsToPercentageValues[dexFieldView.width]}
            >
                <FormInput
                    name={`params.${dexFieldView.name}`}
                    autoFocus={autoFocus}
                    label={dexFieldView.label}
                    control={form.control}
                    rules={dexFieldView.required ? requiredRule : undefined}
                />
            </Box>
        );
    }
}
